import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  LayoutDashboard, 
  Users, 
  Package, 
  Receipt, 
  Calculator, 
  ShoppingCart,
  ShoppingBasket,
  Boxes,
  History,
  LogOut,
  Menu,
  X,
  Wine,
  Shield,
  UserCircle,
  UsersRound,
  ClipboardCheck
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { getChecklistPendenti, getSottoSogliaCount } from '../lib/api';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [checklistPendenti, setChecklistPendenti] = useState(0);
  const [sottoSogliaCount, setSottoSogliaCount] = useState(0);

  const isAdmin = user?.role === 'admin';

  // Fetch checklist pendenti + sotto soglia count
  useEffect(() => {
    const fetchPendenti = async () => {
      try {
        const data = await getChecklistPendenti();
        setChecklistPendenti(data.pendenti);
      } catch (error) {
        console.error('Error fetching checklist pendenti:', error);
      }
    };
    const fetchSottoSoglia = async () => {
      try {
        const data = await getSottoSogliaCount();
        setSottoSogliaCount(data.count);
      } catch (error) {
        console.error('Error fetching sotto soglia count:', error);
      }
    };
    fetchPendenti();
    fetchSottoSoglia();
    const interval = setInterval(() => {
      fetchPendenti();
      fetchSottoSoglia();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Menu items based on role
  const navItems = isAdmin ? [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/fornitori', icon: Users, label: 'Fornitori' },
    { path: '/articoli', icon: Package, label: 'Articoli' },
    { path: '/inventario', icon: Boxes, label: 'Inventario', badge: sottoSogliaCount, badgeVariant: 'destructive' },
    { path: '/personale', icon: UsersRound, label: 'Personale' },
    { path: '/spese', icon: Receipt, label: 'Spese' },
    { path: '/chiusure', icon: Calculator, label: 'Chiusure' },
    { path: '/ordini', icon: ShoppingCart, label: 'Ordini' },
    { path: '/lista-spesa', icon: ShoppingBasket, label: 'Lista Spesa' },
    { path: '/checklist', icon: ClipboardCheck, label: 'Checklist', badge: checklistPendenti },
    { path: '/storico-attivita', icon: History, label: 'Storico Attività' },
    { path: '/utenti', icon: UserCircle, label: 'Utenti' },
  ] : [
    { path: '/staff-dashboard', icon: LayoutDashboard, label: 'Riepilogo' },
    { path: '/spese', icon: Receipt, label: 'Spese' },
    { path: '/chiusure', icon: Calculator, label: 'Chiusure' },
    { path: '/ordini', icon: ShoppingCart, label: 'Ordini' },
    { path: '/lista-spesa', icon: ShoppingBasket, label: 'Lista Spesa' },
    { path: '/inventario', icon: Boxes, label: 'Inventario', badge: sottoSogliaCount, badgeVariant: 'destructive' },
    { path: '/checklist', icon: ClipboardCheck, label: 'Checklist', badge: checklistPendenti },
    { path: '/fornitori', icon: Users, label: 'Fornitori' },
    { path: '/articoli', icon: Package, label: 'Articoli' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden glass sticky top-0 z-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wine className="h-6 w-6 text-primary" strokeWidth={1.5} />
          <span className="font-bold text-lg">Bar Bistrot</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-background/95 backdrop-blur pt-16">
          <nav className="p-4 space-y-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-secondary'
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="h-5 w-5" strokeWidth={1.5} />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <Badge variant="destructive" className="h-5 min-w-5 flex items-center justify-center text-xs">
                    {item.badge}
                  </Badge>
                )}
              </NavLink>
            ))}
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 px-4 py-3 text-destructive hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" strokeWidth={1.5} />
              <span>Esci</span>
            </Button>
          </nav>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex flex-col w-64 min-h-screen bg-card border-r border-border p-4">
          <div className="flex items-center gap-3 px-2 mb-8">
            <Wine className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="font-bold text-xl">Bar Bistrot</h1>
              <p className="text-xs text-muted-foreground">Gestionale</p>
            </div>
          </div>

          <nav className="flex-1 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-secondary text-muted-foreground hover:text-foreground'
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="h-5 w-5" strokeWidth={1.5} />
                <span className="font-medium flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <Badge variant="destructive" className="h-5 min-w-5 flex items-center justify-center text-xs">
                    {item.badge}
                  </Badge>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="border-t border-border pt-4 mt-4">
            <div className="px-3 py-2 mb-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">{user?.nome}</p>
                <Badge variant={isAdmin ? 'default' : 'secondary'} className="text-xs">
                  {isAdmin ? <Shield className="h-3 w-3 mr-1" /> : null}
                  {isAdmin ? 'Admin' : 'Staff'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-btn-desktop"
            >
              <LogOut className="h-5 w-5" strokeWidth={1.5} />
              <span>Esci</span>
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
